import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api';

function CategoryNode({ cat, level, onDelete, onAddChild }) {
  const indent = level * 24;
  const levelColors = ['#4c51bf', '#2b6cb0', '#276749'];

  return (
    <div>
      <div className="card" style={{
        marginLeft: `${indent}px`,
        borderLeft: `4px solid ${levelColors[level] || '#718096'}`,
        marginBottom: '0.5rem',
        padding: '1rem 1.5rem',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <div>
          <div style={{ fontWeight: '600', fontSize: '1rem' }}>
            {level === 1 && '↳ '}
            {level === 2 && '↳↳ '}
            {cat.name}
          </div>
          <div style={{ color: '#718096', fontSize: '0.8rem', marginTop: '0.2rem' }}>
            {cat.type === 'language' ? '🌍 Sprache' : '📖 Thema'} · {cat.card_count} Karten
          </div>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {level < 2 && (
            <button
              className="btn-secondary"
              style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem' }}
              onClick={() => onAddChild(cat)}
            >
              + Unterkategorie
            </button>
          )}
          <button className="btn-danger" onClick={() => onDelete(cat.id)}>🗑</button>
        </div>
      </div>
      {cat.children && cat.children.map(child => (
        <CategoryNode
          key={child.id}
          cat={child}
          level={level + 1}
          onDelete={onDelete}
          onAddChild={onAddChild}
        />
      ))}
    </div>
  );
}

function Categories() {
  const [tree, setTree] = useState([]);
  const [flat, setFlat] = useState([]);
  const [name, setName] = useState('');
  const [type, setType] = useState('language');
  const [parentId, setParentId] = useState('');
  const [parentLabel, setParentLabel] = useState('');
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API}/categories`);
      setTree(res.data.tree);
      setFlat(res.data.flat);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!name.trim()) return;
    try {
      const res = await axios.post(`${API}/categories`, {
        name,
        type,
        parentId: parentId || null
      });
      setMessage({ type: 'success', text: '✅ Kategorie erstellt!' });
      setName('');
      setParentId('');
      setParentLabel('');
      setTimeout(() => setMessage(null), 3000);
      fetchCategories();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.error || 'Fehler' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Kategorie und alle Unterkategorien löschen?')) return;
    try {
      await axios.delete(`${API}/categories/${id}`);
      fetchCategories();
    } catch (err) {
      console.error(err);
    }
  };

  const handleAddChild = (parent) => {
    setParentId(parent.id);
    setParentLabel(parent.name);
    setName('');
    window.scrollTo(0, 0);
  };

  return (
    <div>
      <h1>Kategorien</h1>

      <div className="card">
        <h2>
          {parentLabel
            ? `Unterkategorie von: ${parentLabel}`
            : 'Neue Hauptkategorie'}
        </h2>

        {parentLabel && (
          <div style={{
            background: '#ebf4ff', borderRadius: '8px',
            padding: '0.5rem 1rem', marginBottom: '1rem',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <span style={{ fontSize: '0.9rem', color: '#2b6cb0' }}>
              📁 Übergeordnet: <strong>{parentLabel}</strong>
            </span>
            <button
              onClick={() => { setParentId(''); setParentLabel(''); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#718096' }}
            >
              ✕ Zurücksetzen
            </button>
          </div>
        )}

        <input
          type="text"
          placeholder={parentLabel ? `Unterkategorie von ${parentLabel}...` : 'z.B. Englisch, Spanisch Urlaub...'}
          value={name}
          onChange={e => setName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
        />

        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <select value={type} onChange={e => setType(e.target.value)} style={{ flex: 1 }}>
            <option value="language">🌍 Sprache</option>
            <option value="topic">📖 Themenbereich</option>
          </select>
          <button className="btn-primary" onClick={handleCreate}>
            + Erstellen
          </button>
        </div>

        {message && <div className={`message message-${message.type}`}>{message.text}</div>}
      </div>

      {loading && <p>Lade Kategorien...</p>}

      {!loading && tree.length === 0 && (
        <div className="card" style={{ textAlign: 'center', color: '#718096' }}>
          <p>Noch keine Kategorien vorhanden.</p>
          <p>Erstelle deine erste Kategorie!</p>
        </div>
      )}

       <div className="card">
        <input
          type="text"
          placeholder="Kategorien durchsuchen..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ marginBottom: 0 }}
        />
      </div>

      {tree
        .filter(cat =>
          search === '' ||
          cat.name.toLowerCase().includes(search.toLowerCase()) ||
          cat.children?.some(child =>
            child.name.toLowerCase().includes(search.toLowerCase()) ||
            child.children?.some(sub =>
              sub.name.toLowerCase().includes(search.toLowerCase())
            )
          )
        )
        .map(cat => (
        <CategoryNode
          key={cat.id}
          cat={cat}
          level={0}
          onDelete={handleDelete}
          onAddChild={handleAddChild}
        />
      ))}
    </div>
  );
}

export default Categories;